import { motion, useReducedMotion } from 'framer-motion'
import type { MenuItem } from './types'

interface DessertsSectionProps {
  id?: string
  title: string
  description?: string
  items: MenuItem[]
}

export function DessertsSection({
  id,
  title,
  description,
  items,
}: DessertsSectionProps) {
  const shouldReduceMotion = useReducedMotion()

  const baseTransition = { duration: 0.6, ease: 'easeOut' as const }
  const fadeUp = shouldReduceMotion
    ? {}
    : {
        initial: { opacity: 0, y: 28 },
        whileInView: { opacity: 1, y: 0 },
        viewport: { once: true, margin: '-80px' },
        transition: baseTransition,
      }

  return (
    <section
      id={id}
      className="bg-white px-4 py-[clamp(4.5rem,10vw,8rem)] sm:px-6 lg:px-8"
      aria-labelledby={`${id}-heading`}
    >
      <div className="mx-auto max-w-6xl">
        {/* Header */}
        <motion.div {...fadeUp} className="mb-12 text-center sm:mb-16">
          <h2
            id={`${id}-heading`}
            className="font-display text-[clamp(2.5rem,7vw,4.5rem)] font-extrabold leading-[0.95] tracking-tighter text-ink"
          >
            {title}
          </h2>
          {description && (
            <p className="mx-auto mt-6 max-w-2xl text-lg text-muted">{description}</p>
          )}
        </motion.div>

        {/* Polaroid strip */}
        <motion.div
          {...fadeUp}
          className="-mx-4 flex gap-5 overflow-x-auto px-4 pb-6 pt-2 sm:-mx-6 sm:px-6 lg:mx-0 lg:grid lg:grid-cols-5 lg:gap-6 lg:overflow-visible lg:px-0"
        >
          {items.map((item, index) => (
            <article
              key={item.id ?? item.title}
              className={`
                snap-start shrink-0
                w-[60vw] sm:w-[38vw]
                bg-cream p-3 rounded-2xl shadow-sm
                transition-all duration-300
                hover:-translate-y-1 hover:shadow-lg
                lg:w-auto
                ${index % 2 === 0 ? '-rotate-1' : 'rotate-1'}
              `}
            >
              <div className="relative aspect-[3/4] w-full overflow-hidden rounded-xl bg-silver">
                {item.image ? (
                  <img
                    src={item.image}
                    alt={item.title}
                    className="h-full w-full object-cover transition-transform duration-700 hover:scale-105"
                    loading="lazy"
                  />
                ) : (
                  <div className="h-full w-full bg-silver" />
                )}
              </div>
              <div className="p-3 text-center">
                <h3 className="font-display text-lg font-semibold leading-tight text-ink">
                  {item.title}
                </h3>
                {item.price && (
                  <p className="mt-1 text-sm font-bold text-ink">{item.price}</p>
                )}
                {item.description && (
                  <p className="mt-1 text-sm leading-relaxed text-muted">{item.description}</p>
                )}
              </div>
            </article>
          ))}
        </motion.div>
      </div>
    </section>
  )
}
